import { cloneDeep, round } from 'lodash'
import type { ReduxQuote, ReduxRecapInfo } from 'src/types/apiType'
import type { QuoteType } from 'src/types/miscType'
import { updatedParseFloat } from 'src/utils/miscellaneous'
import { getCreateQuoteRecapInfoUtil } from 'src/utils/recapGridUtils/createQuoteRecapInfoUtil/getCreateQuoteRecapInfoUtil'
import { calculateRowTotal } from 'src/utils/recapGridUtils/updateRecapGrid'

const parseFloatAttributes = (row: any) => {
	const localRow = { ...row }
	if (localRow.provisionalTotal || localRow.provisionalTotal === '')
		localRow.provisionalTotal = updatedParseFloat(localRow.provisionalTotal)
	if (localRow.actualTotal || localRow.actualTotal === '')
		localRow.actualTotal = updatedParseFloat(localRow.actualTotal)
	return localRow as ReduxRecapInfo
}

const calculateGridTotals1_6 = ({
	rows,
	quoteType,
	quoteDetails,
}: {
	rows: ReduxRecapInfo[]
	quoteType: QuoteType
	quoteDetails?: ReduxQuote | null
}) => {
	let localRows = cloneDeep(rows)

	// update Prov total
	if (quoteDetails?.provisionalColumn === 'true') {
		localRows = updateProvisionalTotal(localRows)
	}
	localRows = updatePayrollTaxes({ rows: localRows, quoteType })

	// update sub category totals
	localRows = updateSubCategories(localRows)
	// update category totals
	localRows = updateCategories(localRows)

	return localRows
}

const updateProvisionalTotal = (rows: ReduxRecapInfo[]) => {
	const localRows = cloneDeep(rows)
	return localRows.map((row) => {
		if (
			row.costLineType === 'costLine' &&
			row.provisionalQuantity &&
			(row.provisionalNumber || row.provisionalShootNumber) &&
			row.provisionalRate
		) {
			row.provisionalTotal = calculateRowTotal(row)
		}
		return row
	})
}

const updateSubCategories = (rows: ReduxRecapInfo[]) => {
	let localRows = cloneDeep(rows)
	const subCategoryTotalRows = localRows.filter(
		(row) => row.costLineType === 'subCategoryTotal'
	)
	localRows = subCategoryTotalRows.reduce((localRows, row) => {
		if (!row?.category || !row.subCategory) {
			return localRows
		}
		localRows = updateSubCategoryTotal(
			localRows,
			row.category,
			row.subCategory
		)
		return localRows
	}, localRows)
	return localRows
}

const updateCategories = (rows: ReduxRecapInfo[]) => {
	let localRows = cloneDeep(rows)
	const categoryTotalRows = localRows.filter(
		(row) => row.costLineType === 'categoryTotal'
	)
	localRows = categoryTotalRows.reduce((localRows, row) => {
		if (!row?.category) {
			return localRows
		}
		localRows = updateCategoryTotal(localRows, row.category)
		return localRows
	}, localRows)
	return localRows
}

function getPayrollTaxRateMap() {
	const frenchApfpPayrollTaxCostlineNames = [
		'Shooting crew',
		'Director',
		'Talent',
		'Crew - Postproduction - Image',
		'Crew - Postproduction - Sound',
	]

	const payrollTotalObjects = {
		provisionalTotal: 0,
		actualTotal: 0,
	}
	const payrollTaxRateMap = frenchApfpPayrollTaxCostlineNames.reduce(
		(payrollTaxRateMap, payrollTaxCostlineName) => {
			payrollTaxRateMap[payrollTaxCostlineName] = {
				local: cloneDeep(payrollTotalObjects),
				international: cloneDeep(payrollTotalObjects),
			}
			return payrollTaxRateMap
		},
		{} as {
			[key: string]: {
				local: typeof payrollTotalObjects
				international: typeof payrollTotalObjects
			}
		}
	)
	return payrollTaxRateMap
}

const updatePayrollTaxes = ({
	rows,
	quoteType,
}: {
	rows: ReduxRecapInfo[]
	quoteType: QuoteType
}) => {
	const localRows = cloneDeep(rows)
	const payrollTaxRateMap = getPayrollTaxRateMap()

	for (const r of localRows) {
		if (r.provisionalPayrollTaxType) {
			if (r.isInternational) {
				payrollTaxRateMap[
					r.provisionalPayrollTaxType
				].international.provisionalTotal += r?.provisionalTotal ?? 0
			} else {
				payrollTaxRateMap[
					r.provisionalPayrollTaxType
				].local.provisionalTotal += r?.provisionalTotal ?? 0
			}
		}
		if (r.actualPayrollTaxType) {
			if (r.isInternational) {
				payrollTaxRateMap[
					r.actualPayrollTaxType
				].international.actualTotal += r?.actualTotal ?? 0
			} else {
				payrollTaxRateMap[r.actualPayrollTaxType].local.actualTotal +=
					r?.actualTotal ?? 0
			}
		}
	}

	for (const payrollTaxItem of Object.keys(payrollTaxRateMap)) {
		// 2 parts 1 for local and another for international
		const payrollTaxItemLocalIndexInRows = localRows.findIndex(
			(r) => r.category === 'Payroll Taxes' && r.item === payrollTaxItem
		)
		const payrollTaxItemIntIndexInRows = localRows.findIndex(
			(r) =>
				r.category === 'Payroll Taxes - EXE' &&
				r.item === payrollTaxItem
		)
		if (payrollTaxItemLocalIndexInRows !== -1) {
			const localValues = payrollTaxRateMap[payrollTaxItem].local
			const payrollTaxItemProvisionalTotal = round(
				(localRows[payrollTaxItemLocalIndexInRows].quantity ?? 0) *
					localValues.provisionalTotal,
				2
			)
			const payrollTaxItemActualTotal = round(
				(localRows[payrollTaxItemLocalIndexInRows].quantity ?? 0) *
					localValues.actualTotal,
				2
			)
			localRows[payrollTaxItemLocalIndexInRows].provisionalTotal =
				payrollTaxItemProvisionalTotal
			localRows[payrollTaxItemLocalIndexInRows].actualTotal =
				payrollTaxItemActualTotal
		}

		if (payrollTaxItemIntIndexInRows !== -1) {
			const internationalValues =
				payrollTaxRateMap[payrollTaxItem].international
			const payrollTaxItemProvisionalTotal = round(
				(localRows[payrollTaxItemIntIndexInRows].quantity ?? 0) *
					internationalValues.provisionalTotal,
				2
			)
			const payrollTaxItemActualTotal = round(
				(localRows[payrollTaxItemIntIndexInRows].quantity ?? 0) *
					internationalValues.actualTotal,
				2
			)
			localRows[payrollTaxItemIntIndexInRows].provisionalTotal =
				payrollTaxItemProvisionalTotal
			localRows[payrollTaxItemIntIndexInRows].actualTotal =
				payrollTaxItemActualTotal
		}
	}

	return localRows
}

const updateSubCategoryTotal = (
	rows: ReduxRecapInfo[],
	category: string,
	subCategory: string
) => {
	const localRows = cloneDeep(rows)
	const subCategoryTotalsObject = localRows.reduce(
		(totalsObject, r) => {
			if (
				r.category === category &&
				r.subCategory === subCategory &&
				(r.costLineType === 'costLine' ||
					r.costLineType === 'subCategoryTaxes') &&
				(r.provisionalTotal || r.actualTotal || r.purchaseTotal)
			) {
				if (r.provisionalTotal)
					totalsObject.provisionalTotal += r.provisionalTotal
				if (r.actualTotal) totalsObject.actualTotal += r.actualTotal
				if (r.purchaseTotal)
					totalsObject.purchaseTotal += r.purchaseTotal
			}
			return totalsObject
		},
		{ provisionalTotal: 0, actualTotal: 0, purchaseTotal: 0 }
	)
	subCategoryTotalsObject.provisionalTotal = round(
		subCategoryTotalsObject.provisionalTotal,
		2
	)
	subCategoryTotalsObject.actualTotal = round(
		subCategoryTotalsObject.actualTotal,
		2
	)
	subCategoryTotalsObject.purchaseTotal = round(
		subCategoryTotalsObject.purchaseTotal,
		2
	)

	const subCategoryTotalRowIndex = localRows.findIndex(
		(r) =>
			r.costLineType === 'subCategoryTotal' &&
			r.category === category &&
			r.subCategory === subCategory
	)
	localRows[subCategoryTotalRowIndex].provisionalTotal =
		subCategoryTotalsObject.provisionalTotal
	localRows[subCategoryTotalRowIndex].actualTotal =
		subCategoryTotalsObject.actualTotal
	localRows[subCategoryTotalRowIndex].purchaseTotal =
		subCategoryTotalsObject.purchaseTotal

	return localRows
}

const updateCategoryTotal = (rows: ReduxRecapInfo[], category: string) => {
	const localRows = cloneDeep(rows)
	const categoryTotalsObject = localRows.reduce(
		(totalsObject, r) => {
			if (
				r.category === category &&
				r.costLineType === 'subCategoryTotal' &&
				(r.provisionalTotal || r.actualTotal || r.purchaseTotal)
			) {
				if (r.provisionalTotal)
					totalsObject.provisionalTotal += r.provisionalTotal
				if (r.actualTotal) totalsObject.actualTotal += r.actualTotal
				if (r.purchaseTotal)
					totalsObject.purchaseTotal += r.purchaseTotal
			}
			return totalsObject
		},
		{ provisionalTotal: 0, actualTotal: 0, purchaseTotal: 0 }
	)
	categoryTotalsObject.provisionalTotal = round(
		categoryTotalsObject.provisionalTotal,
		2
	)
	categoryTotalsObject.actualTotal = round(
		categoryTotalsObject.actualTotal,
		2
	)
	categoryTotalsObject.purchaseTotal = round(
		categoryTotalsObject.purchaseTotal,
		2
	)

	const categoryTotalRowIndex = localRows.findIndex(
		(r) => r.costLineType === 'categoryTotal' && r.category === category
	)
	//
	localRows[categoryTotalRowIndex].provisionalTotal =
		categoryTotalsObject.provisionalTotal
	localRows[categoryTotalRowIndex].actualTotal =
		categoryTotalsObject.actualTotal
	localRows[categoryTotalRowIndex].purchaseTotal =
		categoryTotalsObject.purchaseTotal

	return localRows
}

const calculateGridTableRowIndex = ({
	rows,
	recalculateId = false,
	quoteType,
}: {
	rows: ReduxRecapInfo[]
	recalculateId?: boolean
	quoteType: QuoteType
}) => {
	let localRows = [...rows]
	for (let i = 0; i < rows.length; i++) {
		localRows[i].tableRowIndex = i
	}
	if (recalculateId) {
		// We find the first costline that has the child freelancers field filled
		const quoteDetails = localRows.find(
			(row) => row?.childFreelancers && row?.childFreelancers?.length > 0
		)
		if (!quoteDetails?.quoteId || quoteDetails?.childFreelancers) {
			return localRows
		}
		const { createRecapInfoCostLineIdsAndLabels } =
			getCreateQuoteRecapInfoUtil(quoteType)

		localRows = createRecapInfoCostLineIdsAndLabels(
			localRows,
			quoteDetails?.quoteId,
			quoteDetails?.tenantId,
			quoteDetails?.childFreelancers as string[]
		)
	}
	return localRows
}

const calculateGridTotalsAndIndex = ({
	rows,
	recalculateId,
	quoteType,
}: {
	rows: ReduxRecapInfo[]
	recalculateId?: boolean
	quoteType: QuoteType
}) => {
	let localRows = [...rows]
	localRows = calculateGridTotals1_6({ rows: localRows, quoteType })
	localRows = calculateGridTableRowIndex({
		rows: localRows,
		recalculateId,
		quoteType,
	})
	return localRows
}

export {
	calculateGridTotals1_6,
	calculateGridTotalsAndIndex,
	parseFloatAttributes,
}
